import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, Tag } from "lucide-react";
import { toast } from "sonner";
import { api, brl, img, errMsg, useSeo } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { ShippingCalc } from "./ProductPage";

export const toPayload = (cart) => cart.map((i) => ({ product_id: i.product_id, qty: i.qty, options: i.options, colors: i.colors, custom: i.custom }));

export function Summary({ pr, children }) {
  if (!pr) return null;
  return (
    <div className="space-y-2.5 text-sm" data-testid="order-summary">
      <div className="flex justify-between"><span className="text-[#A0A0A0]">Subtotal</span><span data-testid="summary-subtotal">{brl(pr.subtotal)}</span></div>
      {pr.discount > 0 && <div className="flex justify-between text-[#00C8FF]"><span>Desconto {pr.coupon && `(${pr.coupon})`}</span><span data-testid="summary-discount">-{brl(pr.discount)}</span></div>}
      <div className="flex justify-between"><span className="text-[#A0A0A0]">Frete</span><span data-testid="summary-shipping">{pr.shipping_method ? (pr.shipping ? brl(pr.shipping) : "Grátis") : "A calcular"}</span></div>
      <div className="flex justify-between text-lg font-bold pt-3 border-t border-[#282D37]"><span>Total</span><span data-testid="summary-total">{brl(pr.total)}</span></div>
      {pr.savings > 0 && <div className="text-xs text-[#00C8FF] text-right" data-testid="summary-savings">Você economiza {brl(pr.savings)}</div>}
      {children}
    </div>
  );
}

export default function Cart() {
  const { cart, updateQty, removeItem } = useStore();
  const [coupon, setCoupon] = useState(localStorage.getItem("e3_coupon") || "");
  const [pr, setPr] = useState(null);
  const nav = useNavigate();
  useSeo({ title: "Carrinho" });
  const price = async (c = coupon) => {
    if (!cart.length) return setPr(null);
    try { setPr((await api.post("/cart/price", { items: toPayload(cart), coupon: c || null })).data); }
    catch (e) { toast.error(errMsg(e)); if (c) { setCoupon(""); localStorage.removeItem("e3_coupon"); setPr((await api.post("/cart/price", { items: toPayload(cart) })).data); } }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { price(); }, [cart]);
  const applyCoupon = () => { localStorage.setItem("e3_coupon", coupon); price(coupon).then(() => coupon && toast.success("Cupom aplicado")); };

  if (!cart.length) return (
    <div className="max-w-xl mx-auto text-center py-32 px-4" data-testid="cart-empty"><h1 className="text-3xl font-bold">Seu carrinho está vazio</h1><p className="text-[#A0A0A0] mt-3">Que tal dar forma a uma nova ideia?</p><Link to="/produtos" className="btn-primary mt-8">Ver produtos</Link></div>
  );
  return (
    <div className="max-w-[1280px] mx-auto px-4 lg:px-8 py-10">
      <h1 className="text-3xl lg:text-4xl font-bold mb-8">Carrinho</h1>
      <div className="grid lg:grid-cols-[1fr_380px] gap-8">
        <div className="space-y-3" data-testid="cart-items">
          {cart.map((i) => (
            <div key={i.key} className="card p-4 flex gap-4" data-testid="cart-item">
              <img src={img(i.image)} alt={i.name} className="h-24 w-24 rounded-xl object-cover" />
              <div className="flex-1 min-w-0">
                <Link to={`/produto/${i.slug}`} className="font-semibold hover:text-[#00C8FF]">{i.name}</Link>
                <div className="text-xs text-[#A0A0A0] mt-1 space-y-0.5">
                  {Object.entries(i.colors || {}).filter(([, v]) => v).map(([k, v]) => <div key={k}>{k}: {v}</div>)}
                  {Object.entries(i.options || {}).map(([k, v]) => <div key={k}>{k}: {v}</div>)}
                  {Object.entries(i.custom || {}).map(([k, v]) => <div key={k}>{k}: {typeof v === "object" ? v.name : v}</div>)}
                </div>
                <div className="flex flex-wrap items-center justify-between gap-3 mt-3">
                  <div className="flex items-center border border-[#282D37] rounded-lg">
                    <button className="p-2.5" aria-label="Diminuir" onClick={() => updateQty(i.key, i.qty - 1)} data-testid="cart-qty-minus"><Minus className="h-3.5 w-3.5" /></button>
                    <span className="w-8 text-center text-sm" data-testid="cart-qty">{i.qty}</span>
                    <button className="p-2.5" aria-label="Aumentar" onClick={() => updateQty(i.key, i.qty + 1)} data-testid="cart-qty-plus"><Plus className="h-3.5 w-3.5" /></button>
                  </div>
                  <div className="text-right"><div className="text-xs text-[#A0A0A0]">{brl(i.unit_price)} un.</div><div className="font-bold">{brl(i.unit_price * i.qty)}</div></div>
                  <button onClick={() => removeItem(i.key)} className="text-[#A0A0A0] hover:text-red-400" aria-label="Excluir" data-testid="cart-remove"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
        <aside className="card p-6 h-fit lg:sticky lg:top-24 space-y-6">
          <div><label className="label">Cupom de desconto</label>
            <div className="flex gap-2"><div className="relative flex-1"><Tag className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#A0A0A0]" /><input className="input pl-9 uppercase" value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} placeholder="CÓDIGO" data-testid="coupon-input" /></div>
              <button className="btn-ghost" onClick={applyCoupon} data-testid="coupon-apply">Aplicar</button></div></div>
          <ShippingCalc items={toPayload(cart)} testid="cart-shipping" />
          <Summary pr={pr} />
          <button className="btn-primary w-full py-4" onClick={() => nav("/checkout")} data-testid="checkout-btn">FINALIZAR COMPRA</button>
        </aside>
      </div>
    </div>
  );
}
